// WS-event → operator-readable notification (NEX-296 / NEX-299 item 5).
//
// The notification center (header bell) and the toast layer both render off the
// SAME global Socket.IO `applyEvent` stream that already drives the query-cache
// merge (apply-event.ts). This module is the single mapping from a raw `WsEvent`
// to a human-readable `Notification` — keep all copy + tone decisions here so the
// bell, the toasts, and the tests agree on one shape. The copy itself is i18n
// (`admin` namespace, `feed.*`); this file holds the KEYS and the tone.

import i18next from 'i18next';
import type { WsEvent } from './apply-event';
import type { DisruptionActiveRun } from '@/lib/disruptions/types';
import { operatorLabel } from '@/routes/disruptions/labels';

export type NotificationTone = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  /** Stable id (monotonic in the store); set by the store, not the mapper. */
  id: string;
  /** Short headline shown in the bell list + toast title. */
  title: string;
  /** Optional secondary line (the affected entity, friendly). */
  description?: string;
  tone: NotificationTone;
  /** True for events worth interrupting the operator with a toast (item 5). */
  toast: boolean;
  /** Optional toast-dedupe key — the bridge raises a toast for a given key AT MOST
   *  once. Kills the per-progress-push "bulk running" spam (one running run pushes
   *  a fresh progress event per group/PNR — thousands). The bell still records all. */
  dedupeKey?: string;
  /** The entity this notification is about, for click-through. */
  caseUrn?: string;
  disruptionUrn?: string;
  /** Epoch ms — set by the store at ingest. */
  at: number;
}

/** The mapper output before the store stamps id/at. */
export type NotificationDraft = Omit<Notification, 'id' | 'at'>;

/**
 * Copy for the feed lives in the `admin` namespace under `feed.*`; this module
 * holds translation KEYS and resolves them through `adminT`, never English
 * literals. `getFixedT(null, ns)` binds the NAMESPACE only, never a language, so
 * each notification is composed in whatever language the console is showing at
 * the moment its event arrives (same contract as `tower-i18n.ts`).
 */
const adminT = i18next.getFixedT(null, 'admin');

/** `feed.caseChanged.*` key per change kind; an unknown kind reads as a plain case. */
const KIND_KEY: Record<string, string> = {
  booking: 'feed.caseChanged.booking',
  rebooking: 'feed.caseChanged.rebooking',
  allocation: 'feed.caseChanged.allocation',
  passenger: 'feed.caseChanged.passenger',
};

/** `feed.bulkRunning.*` key per bulk op — exhaustive over the domain's union, so a
 *  new op cannot ship without its headline. `??` guards a NEWER BFF at runtime. */
const BULK_RUN_KEY: Record<DisruptionActiveRun['op'], string> = {
  rebooking: 'feed.bulkRunning.rebooking',
  allocation: 'feed.bulkRunning.allocation',
  tickets: 'feed.bulkRunning.tickets',
  acceptance: 'feed.bulkRunning.acceptance',
  vouchers: 'feed.bulkRunning.vouchers',
  transfers: 'feed.bulkRunning.transfers',
};

/** Friendly tail of a case URN (`urn:case:LA288-...` → `LA288-...`). */
function caseTail(urn: string): string {
  return urn.split(':').pop() ?? urn;
}

/**
 * Map a raw WS event to a notification draft. Returns `null` for events we don't
 * surface in the feed (none today — every known event is shown, per item 5: "show
 * whatever events the socket receives in a readable form"). Kept as a nullable
 * return so a future noisy/internal event can be filtered without a signature change.
 */
export function toNotification(
  event: WsEvent,
  currentOperatorUrn?: string,
): NotificationDraft | null {
  switch (event.type) {
    case 'DisruptionProgressChanged': {
      const p = event.payload;
      const run = p.activeRun;
      // Keep running operations in the bell; progress already lives on the board.
      if (run) {
        return {
          title: adminT(BULK_RUN_KEY[run.op] ?? 'feed.bulkRunning.generic'),
          description: adminT('feed.startedBy', { operator: operatorLabel(run.operatorUrn) }),
          tone: 'warning',
          toast: false,
          // Collapse repeated per-PNR progress into one bell entry per run.
          dedupeKey: `bulk-running:${run.runId}`,
          disruptionUrn: event.disruptionUrn,
        };
      }
      // Run cleared / steady progress push — quiet feed entry, no toast.
      const pct = Math.round((p.resolvedFraction ?? 0) * 100);
      const done = p.pending === 0 && p.groupsTotal > 0;
      return {
        title: adminT(done ? 'feed.disruptionResolved' : 'feed.disruptionProgress'),
        description: adminT('feed.groupsResolved', {
          resolved: p.resolved,
          total: p.groupsTotal,
          pct,
        }),
        tone: done ? 'success' : 'info',
        toast: done,
        // The "resolved" toast fires ONCE per disruption+run: every progress push
        // (each page load re-joins the room and gets a snapshot) re-carries the
        // 100% state, which stacked a fresh "Disruption resolved" toast on every
        // navigation. The steady/in-progress entries stay un-deduped (quiet feed).
        ...(done
          ? { dedupeKey: `disruption-resolved:${event.disruptionUrn}:${p.runRunId ?? 'none'}` }
          : {}),
        disruptionUrn: event.disruptionUrn,
      };
    }

    case 'StateChanged': {
      const holder = event.payload.locked_by;
      // Suppress YOUR OWN lock. Every write auto-acquires the record lock and
      // broadcasts a StateChanged to the case room — including back to you. Without
      // this check, doing your own edit toasts "Case locked by another operator",
      // attributing your own lock to a stranger (the holder URN is YOU).
      if (holder && currentOperatorUrn && holder.operatorUrn === currentOperatorUrn) {
        return null;
      }
      const who = holder?.displayName;
      return {
        title: adminT(holder ? 'feed.caseLocked' : 'feed.caseLockReleased'),
        description: who ? `${who} · ${caseTail(event.caseUrn)}` : caseTail(event.caseUrn),
        tone: holder ? 'warning' : 'info',
        toast: Boolean(holder),
        caseUrn: event.caseUrn,
      };
    }

    case 'CaseChanged': {
      return {
        title: adminT(KIND_KEY[event.kind] ?? 'feed.caseChanged.case'),
        description: caseTail(event.subCaseUrn ?? event.caseUrn),
        tone: 'info',
        toast: false,
        caseUrn: event.caseUrn,
      };
    }

    case 'BookingConfirmed': {
      return {
        title: adminT('feed.bookingConfirmed'),
        description: caseTail(event.caseUrn),
        tone: 'success',
        toast: true,
        caseUrn: event.caseUrn,
      };
    }

    case 'AllocationProgress': {
      return {
        title: adminT('feed.allocationProgress'),
        description: caseTail(event.caseUrn),
        tone: 'info',
        toast: false,
        caseUrn: event.caseUrn,
      };
    }

    case 'PassengerStatusChanged': {
      return {
        title: adminT('feed.passengerStatusChanged'),
        description: caseTail(event.caseUrn),
        tone: 'info',
        toast: false,
        caseUrn: event.caseUrn,
      };
    }

    case 'DisruptionAllocationPlanChanged': {
      // Another operator built or booked the SHARED hotel plan — surface it so the
      // swarm knows who's acting on accommodation for this event. Suppress your own
      // action (you already know you did it).
      const p = event.payload;
      const actorUrn = p.bookedByUrn ?? p.builtByUrn;
      if (currentOperatorUrn && actorUrn === currentOperatorUrn) {
        return null;
      }
      if (p.results) {
        const booked = p.results.filter((res) => res.status === 'BOOKED').length;
        return {
          title: adminT('feed.hotelPlan.booked'),
          description: adminT('feed.hotelPlan.bookedDetail', {
            name: p.bookedByName ?? p.builtByName,
            count: booked,
          }),
          tone: 'success',
          toast: true,
          disruptionUrn: event.disruptionUrn,
        };
      }
      return {
        title: adminT('feed.hotelPlan.built'),
        description: adminT('feed.hotelPlan.builtDetail', {
          name: p.builtByName,
          count: p.plan.totals.groups,
        }),
        tone: 'info',
        toast: false,
        disruptionUrn: event.disruptionUrn,
      };
    }
  }
  // Unknown / unhandled event type → not surfaced in the feed (see contract above).
  return null;
}
